import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authClient } from '../lib/auth';
import { identifyUser, resetAnalytics } from '../lib/analytics';
import { startFlagsForUser, stopFlags } from '../lib/flags';

interface User {
  id: string;
  email: string;
  name?: string;
  image?: string;
  role?: 'user' | 'admin';
  twoFactorEnabled?: boolean;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string) => Promise<{ error?: string }>;
  signInWithOAuth: (provider: 'google' | 'facebook' | 'apple') => Promise<void>;
  signOut: () => Promise<void>;
  setUser: (user: User | null) => void;
  signInWithPassword: (email: string, password: string) => Promise<{ error?: string }>;
  signUpWithPassword: (email: string, password: string, name?: string) => Promise<{ error?: string }>;
  checkSession: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      setUser: (user) => {
        set({ user, isAuthenticated: !!user });
        if (user) {
          identifyUser(user.id, { email: user.email, name: user.name });
          startFlagsForUser(user.id);
        } else {
          stopFlags();
        }
      },
      signIn: async (email: string) => {
        try {
          await authClient.signIn.magicLink({ email, callbackURL: `${window.location.origin}/auth/callback` });
          return {};
        } catch (err: unknown) {
          const message =
            err instanceof Error ? err.message : 'Failed to send magic link';
          return { error: message };
        }
      },
      signInWithPassword: async (email, password) => {
        try {
          const result = await authClient.signIn.email({ email, password });
          if (result.error) return { error: result.error.message ?? 'Failed to sign in' };
          if (result.data?.user) {
            const u = result.data.user as User;
            set({ user: u, isAuthenticated: true });
            identifyUser(u.id, { email: u.email, name: u.name });
            startFlagsForUser(u.id);
          } else {
            // In production, better-auth may not return user data in the sign-in
            // response body (e.g. cross-origin with jwtClient). Fall back to
            // fetching the session so the store is hydrated and navigation fires.
            const session = await authClient.getSession();
            if (session?.data?.user) {
              const u = session.data.user as User;
              set({ user: u, isAuthenticated: true });
              identifyUser(u.id, { email: u.email, name: u.name });
              startFlagsForUser(u.id);
            }
          }
          return {};
        } catch (err) {
          return { error: err instanceof Error ? err.message : 'Failed to sign in' };
        }
      },
      signUpWithPassword: async (email, password, name) => {
        try {
          const result = await authClient.signUp.email({ email, password, name: name ?? '' });
          if (result.error) return { error: result.error.message ?? 'Failed to create account' };
          if (result.data?.user) {
            const u = result.data.user as User;
            set({ user: u, isAuthenticated: true });
            identifyUser(u.id, { email: u.email, name: u.name });
            startFlagsForUser(u.id);
          }
          return {};
        } catch (err) {
          return { error: err instanceof Error ? err.message : 'Failed to create account' };
        }
      },
      signInWithOAuth: async (provider) => {
        await authClient.signIn.social({
          provider,
          callbackURL: `${window.location.origin}/auth/callback`,
        });
      },
      signOut: async () => {
        try {
          await authClient.signOut();
        } catch {
          // Ignore API errors — always clear local state so ProtectedRoute
          // redirects to /login even if the server-side sign-out fails.
        }
        set({ user: null, isAuthenticated: false });
        resetAnalytics();
        stopFlags();
      },
      checkSession: async () => {
        set({ isLoading: true });
        try {
          const session = await authClient.getSession();
          if (session?.data?.user) {
            set({ user: session.data.user as User, isAuthenticated: true });
          } else {
            set({ user: null, isAuthenticated: false });
          }
        } catch {
          set({ user: null, isAuthenticated: false });
        } finally {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
